import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@apollo/client'
import { useFeatureFlag, useFeatureFlagsLoading } from 'src/featureFlags'
import { trackParticipantOpened } from 'src/mixpanel'
import {
  getParticipantListSnapshot,
  getParticipantOpenedProps,
  setParticipantOpenedBands,
  subscribeToParticipantListSnapshot,
} from 'src/mixpanel/participantListContext'
import { MarkFeedSeenDocument, MarkFeedSeenMutation, MarkFeedSeenMutationVariables } from './feedQueries.generated'

const MARK_AS_SEEN_DELAY = 1000

/**
 * Marks the feed seen shortly after it's opened (or its content changes), once its query has
 * come back — `isFeedLoaded`, not the item count, because a feed with nothing in it still
 * counts as viewed while one that is still loading (or failed) has not been seen at all.
 *
 * Auto-marking has to stand down around "mark as unread": feedSeen is append-only
 * and last-seen is the MAX over it, so a markFeedSeen landing after a rollback would
 * silently undo it. `withAutoMarkSeenPaused` handles that ordering for the caller.
 *
 * The first mark-seen of a visit is also what counts as `participant_opened` for analytics:
 * a therapist flipping through participants faster than the delay hasn't opened any of them.
 */
export default function useMarkFeedAsSeen(
  feedID: string,
  feedItems: readonly unknown[],
  isFeedLoaded: boolean,
  mutation = MarkFeedSeenDocument,
  { waitForParticipantList = true }: { waitForParticipantList?: boolean } = {}
) {
  const [markFeedSeen] = useMutation<MarkFeedSeenMutation, MarkFeedSeenMutationVariables>(mutation, {
    variables: {
      uid: feedID,
    },
  })

  // Two reasons not to auto-mark-seen, each answering one question:
  // - did the provider mark something unread in this session? (permanent, until reopened)
  // - is a mark-as-unread in flight right now? (count, so overlapping calls can't
  //   un-pause each other)
  const markedUnreadRef = useRef(false)
  const unreadInFlightRef = useRef(0)
  // The latest markFeedSeen, so a rollback can let it land first and still be last. Never
  // cleared: awaiting an already-settled request costs nothing.
  const latestSeenRef = useRef<Promise<unknown>>(Promise.resolve())
  // Whether this visit's participant_opened has been sent.
  const openedRef = useRef(false)

  // On the new list UI the timer also waits for the participant list to have loaded. Marking
  // seen writes this visit's lastViewedAt and unread count into the cache the list snapshot is
  // built from, so a participant_opened read after that would describe the state after the
  // visit — and on a cold start the feed often loads well before the list does.
  const hasPriorityFilters = useFeatureFlag('featureFlagDashboardPriorityFilters')
  const featureFlagsLoading = useFeatureFlagsLoading()
  const [isListPublished, setIsListPublished] = useState(() => getParticipantListSnapshot() !== null)
  useEffect(() => {
    const update = () => setIsListPublished(getParticipantListSnapshot() !== null)
    update()
    return subscribeToParticipantListSnapshot(update)
  }, [])
  const canMarkSeen =
    isFeedLoaded && !featureFlagsLoading && (!waitForParticipantList || !hasPriorityFilters || isListPublished)

  // Both reset on every switch: coming back to a participant is a new visit, however brief the
  // detour was.
  useEffect(() => {
    markedUnreadRef.current = false
    openedRef.current = false
    setParticipantOpenedBands(null)
  }, [feedID])

  // The bands chat_send reuses for this visit, taken as soon as the list has this participant
  // and kept in step with the list until participant_opened goes out — after that, marking
  // seen starts clearing indicators and the list no longer describes why the participant was
  // opened. The list republishes in its own effect, so the first attempt can run before the
  // snapshot points at this participant.
  useEffect(() => {
    const capture = () => {
      if (!canMarkSeen || openedRef.current) {
        return
      }
      const openedProps = getParticipantOpenedProps(feedID)
      if (openedProps) {
        setParticipantOpenedBands({ uid: feedID, bands: openedProps.patient_priority_bands })
      }
    }
    capture()
    return subscribeToParticipantListSnapshot(capture)
  }, [feedID, canMarkSeen])

  /**
   * Runs `task` (a mark-as-unread) with auto-mark-seen paused, after any in-flight
   * markFeedSeen has landed — so the task's write to the marker is the last one.
   *
   * How long the pause lasts depends on what `task` reports:
   * - `true`  — it rolled the feed back to unread, so auto-mark-seen stays paused for
   *             the rest of this viewing session (reset when the feed is reopened).
   * - `false` — nothing changed, so auto-mark-seen resumes.
   *
   * Once a task has reported `true`, later ones can't un-pause it.
   */
  const withAutoMarkSeenPaused = useCallback(async (task: () => Promise<boolean>) => {
    unreadInFlightRef.current += 1
    try {
      await latestSeenRef.current
      if (await task()) {
        markedUnreadRef.current = true
      }
    } finally {
      unreadInFlightRef.current -= 1
    }
  }, [])

  useEffect(() => {
    // Nothing has been seen until the feed is on screen; marking it now would clear unread
    // messages the coach never got to read.
    if (!canMarkSeen) {
      return undefined
    }

    // If feed is still active after timeout, mark it as seen
    const timeout = setTimeout(() => {
      if (markedUnreadRef.current || unreadInFlightRef.current > 0) {
        return
      }
      // Read before the save lands so `viewed_today` still describes the state before this view.
      if (!openedRef.current) {
        openedRef.current = true
        const openedProps = getParticipantOpenedProps(feedID)
        if (openedProps) {
          trackParticipantOpened(openedProps)
        }
      }
      // .catch so a failed request doesn't reject whoever awaits latestSeenRef.
      latestSeenRef.current = markFeedSeen().catch(() => undefined)
    }, MARK_AS_SEEN_DELAY)
    return () => {
      clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedID, feedItems, canMarkSeen])

  return { withAutoMarkSeenPaused }
}
